import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';

interface ReportRow {
  id: string;
  company_id: string;
  company_name: string;
  report_type: 'product_discovery' | 'lead_generation';
  week_start: string;
  status: string;
  pipeline_status: string;
  pipeline_error: string | null;
  ai_generated_at: string | null;
}

type StatusFilter = 'csr_review' | 'published' | 'all';

function formatWeek(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft:       { label: 'Draft',      className: 'bg-gray-100 text-gray-600' },
  csr_review:  { label: 'Needs Review', className: 'bg-amber-100 text-amber-700' },
  published:   { label: 'Published',  className: 'bg-green-100 text-green-700' },
  skipped:     { label: 'Skipped',    className: 'bg-gray-100 text-gray-400' },
};

const TYPE_LABEL: Record<string, string> = {
  product_discovery: '🔬 Product Discovery',
  lead_generation:   '🎯 Lead Generation',
};

// Returns the Monday of the current week as YYYY-MM-DD
function currentWeekMonday(): string {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

const CSRReports: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [companies, setCompanies] = useState<Record<string, string>>({});
  const [companyList, setCompanyList] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('csr_review');

  // Run Report modal state
  const [showRunModal, setShowRunModal] = useState(false);
  const [runCompanyId, setRunCompanyId] = useState('');
  const [runWeekStart, setRunWeekStart] = useState(currentWeekMonday());
  const [runAllTime, setRunAllTime] = useState(false);
  const [runResponseStart, setRunResponseStart] = useState('');
  const [runResponseEnd, setRunResponseEnd] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);

  const loadReports = useCallback(async (companyMap: Record<string, string>) => {
    let query = supabase
      .from('customer_reports')
      .select('id, company_id, report_type, week_start, status, pipeline_status, pipeline_error, ai_generated_at')
      .order('week_start', { ascending: false });

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    } else {
      query = query.in('status', ['csr_review', 'published', 'skipped']);
    }

    const { data } = await query;
    setReports(
      (data ?? []).map((r: any) => ({
        ...r,
        company_name: companyMap[r.company_id] ?? 'Unknown',
      }))
    );
  }, [statusFilter]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: companyData } = await supabase
        .from('companies')
        .select('id, name')
        .eq('assigned_csr_id', user.id);

      const list = companyData ?? [];
      const companyMap: Record<string, string> = {};
      list.forEach((c: any) => { companyMap[c.id] = c.name; });
      setCompanies(companyMap);
      setCompanyList(list);
      if (list.length > 0) setRunCompanyId(list[0].id);

      await loadReports(companyMap);
      setLoading(false);
    };
    load();
  }, [statusFilter, loadReports]);

  const handleRunReport = async () => {
    if (!runCompanyId) return;
    setRunning(true);
    setRunResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/synthesize-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            company_id: runCompanyId,
            week_start: runWeekStart,
            all_time: runAllTime,
            ...(!runAllTime && runResponseStart ? { response_start: runResponseStart } : {}),
            ...(!runAllTime && runResponseEnd ? { response_end: runResponseEnd } : {}),
          }),
        }
      );
      const json = await res.json();
      if (!res.ok) {
        setRunResult(`Error: ${json.error ?? res.statusText}`);
      } else {
        const completed = (json.results ?? []).filter((r: any) => r.status === 'complete');
        setRunResult(`Done — ${completed.length} report(s) generated.`);
        // Reload report list
        const companyMap: Record<string, string> = {};
        companyList.forEach(c => { companyMap[c.id] = c.name; });
        await loadReports(companyMap);
      }
    } catch (e: any) {
      setRunResult(`Error: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const grouped = reports.reduce<Record<string, ReportRow[]>>((acc, r) => {
    const week = r.week_start;
    acc[week] = acc[week] ?? [];
    acc[week].push(r);
    return acc;
  }, {});

  const weekKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Weekly Reports</h1>
          <p className="text-sm text-gray-500 mt-1">AI-generated intelligence reports for your assigned companies.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowRunModal(true); setRunResult(null); }}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Run Report
          </button>
          {(['csr_review', 'published', 'all'] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === f
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'csr_review' ? 'Needs Review' : f === 'published' ? 'Published' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Run Report modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Run Report</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select
                  value={runCompanyId}
                  onChange={e => setRunCompanyId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {companyList.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Week of</label>
                <input
                  type="date"
                  value={runWeekStart}
                  onChange={e => setRunWeekStart(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-400 mt-1">Use the Monday of the target week.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runAllTime}
                  onChange={e => setRunAllTime(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600"
                />
                <span className="text-sm text-gray-700">All-time responses (ignore all date filters)</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response date range <span className="text-gray-400 font-normal">(optional — overrides week filter)</span>
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    value={runResponseStart}
                    onChange={e => setRunResponseStart(e.target.value)}
                    disabled={runAllTime}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <span className="text-gray-400 text-sm">–</span>
                  <input
                    type="date"
                    value={runResponseEnd}
                    onChange={e => setRunResponseEnd(e.target.value)}
                    disabled={runAllTime}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Leave blank to use the week above. Set only a start date to pull from that date onward.</p>
              </div>
              {runResult && (
                <p className={`text-sm rounded-lg px-3 py-2 ${runResult.startsWith('Error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                  {runResult}
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleRunReport}
                disabled={running || !runCompanyId}
                className="flex-1 bg-indigo-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {running ? 'Running...' : 'Run Report'}
              </button>
              <button
                onClick={() => setShowRunModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 text-sm font-medium py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {loading ? (
        <div className="text-center py-12 text-sm text-gray-500">Loading reports...</div>
      ) : weekKeys.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400">
          No reports found. Reports are generated automatically every Sunday.
        </div>
      ) : (
        <div className="space-y-8">
          {weekKeys.map(week => (
            <div key={week}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Week of {formatWeek(week)}
              </h2>
              <div className="space-y-3">
                {grouped[week].map(report => {
                  const badge = STATUS_BADGE[report.status] ?? STATUS_BADGE.draft;
                  return (
                    <div
                      key={report.id}
                      className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-5 py-4"
                    >
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-900">{report.company_name}</span>
                          <span className="text-sm text-gray-500">{TYPE_LABEL[report.report_type]}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                          {report.ai_generated_at && (
                            <span className="text-xs text-gray-400">
                              Generated {new Date(report.ai_generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          {report.pipeline_status === 'failed' && (
                            <span className="text-xs text-red-500">
                              Pipeline failed{report.pipeline_error ? `: ${report.pipeline_error.slice(0, 60)}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/csr-reports/${report.id}`)}
                        className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {report.status === 'published' ? 'View' : 'Review'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CSRReports;
